/**
 * Provider specs: everything that differs between the AI services qda can talk
 * to, so `makeAiClient` can be one function and the settings page only has to
 * ask for an API key.
 *
 * The provider list mirrors yaqatw's service/ai/providers.ts (the two apps stay
 * feature-comparable) but the code is deliberately independent — qda has its
 * own keys, its own settings page and no dependency on the plugin iframe.
 *
 * Everything goes through `fetch` rather than a vendor SDK: five of the six
 * entries have no browser SDK worth the bundle weight in a PWA, and mixing one
 * vendor's SDK with hand-rolled requests for the rest would leave two ways to
 * do the same thing. The Anthropic request shape below is the documented
 * direct-from-browser form.
 */

import { aiFetch, cleanJsonResponse, readPath } from "./aiClient";

/** How a provider's request body and response are shaped. */
export type AiBodyStyle = "openai" | "anthropic" | "gemini";

export interface ProviderSpec {
  key: string;
  label: string;
  bodyStyle: AiBodyStyle;
  /** `{model}` and `{key}` are substituted at request time. */
  url: string;
  defaultModel: string;
  /** Header carrying the credential; "" when the key rides in the URL (Gemini). */
  authHeader: string;
  authPrefix: string;
  extraHeaders?: Record<string, string>;
}

/**
 * Model defaults are the current flagship of each provider at the time of
 * writing; every one is overridable in Settings > AI, which is where a user
 * picks something cheaper or newer without a code change.
 */
export const PROVIDER_SPECS: ProviderSpec[] = [
  {
    key: "claude",
    label: "Claude (Anthropic)",
    bodyStyle: "anthropic",
    url: "https://api.anthropic.com/v1/messages",
    defaultModel: "claude-opus-5",
    authHeader: "x-api-key",
    authPrefix: "",
    // Anthropic blocks browser-origin requests unless this opt-in is present —
    // it acknowledges that the key is exposed to the page, which is the same
    // trade every credential in settingsStore already makes.
    extraHeaders: {
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
  },
  {
    key: "openai",
    label: "ChatGPT (OpenAI)",
    bodyStyle: "openai",
    url: "https://api.openai.com/v1/chat/completions",
    defaultModel: "gpt-4o",
    authHeader: "Authorization",
    authPrefix: "Bearer ",
  },
  {
    key: "gemini",
    label: "Gemini (Google)",
    bodyStyle: "gemini",
    url: "https://generativelanguage.googleapis.com/v1beta/models/{model}:generateContent?key={key}",
    defaultModel: "gemini-2.0-flash",
    authHeader: "",
    authPrefix: "",
  },
  {
    key: "groq",
    label: "Groq",
    bodyStyle: "openai",
    url: "https://api.groq.com/openai/v1/chat/completions",
    defaultModel: "llama-3.3-70b-versatile",
    authHeader: "Authorization",
    authPrefix: "Bearer ",
  },
  {
    key: "deepseek",
    label: "DeepSeek",
    bodyStyle: "openai",
    url: "https://api.deepseek.com/chat/completions",
    defaultModel: "deepseek-chat",
    authHeader: "Authorization",
    authPrefix: "Bearer ",
  },
];

export function findProviderSpec(key: string): ProviderSpec | undefined {
  return PROVIDER_SPECS.find((spec) => spec.key === key);
}

/**
 * Claude models where thinking is on by default and `max_tokens` is a ceiling
 * on thinking *plus* the answer — a short JSON fragment can otherwise truncate
 * mid-object while the model is still reasoning. `effort: "low"` keeps these
 * requests fast and cheap without disabling thinking outright (disabling it can
 * leak `<thinking>` tags into the text, which would break the JSON parse).
 * Older models reject `output_config`, hence the prefix check.
 */
function supportsEffort(model: string): boolean {
  return /^claude-(opus-5|opus-4-[5678]|sonnet-5|sonnet-4-6|fable-5|mythos-5)/.test(model);
}

export interface AiRequestConfig {
  url: string;
  headers: Record<string, string>;
  model: string;
  maxTokens: number;
  /** Dotted path to the text in the response; blank = the body style's default. */
  responsePath?: string;
}

function buildBody(style: AiBodyStyle, cfg: AiRequestConfig, system: string, user: string): unknown {
  if (style === "anthropic") {
    const body: Record<string, unknown> = {
      model: cfg.model,
      max_tokens: cfg.maxTokens,
      system,
      messages: [{ role: "user", content: user }],
    };
    if (supportsEffort(cfg.model)) body.output_config = { effort: "low" };
    return body;
  }

  if (style === "gemini") {
    return {
      system_instruction: { parts: [{ text: system }] },
      contents: [{ role: "user", parts: [{ text: user }] }],
      generationConfig: { maxOutputTokens: cfg.maxTokens },
    };
  }

  // OpenAI-compatible: the system prompt is a leading message rather than a
  // dedicated field. Covers OpenAI, Groq, DeepSeek and most self-hosted
  // gateways (Ollama, LM Studio, OpenRouter).
  return {
    model: cfg.model,
    max_tokens: cfg.maxTokens,
    messages: [
      { role: "system", content: system },
      { role: "user", content: user },
    ],
  };
}

const DEFAULT_RESPONSE_PATHS: Record<AiBodyStyle, string> = {
  openai: "choices.0.message.content",
  anthropic: "", // handled specially below — the text block isn't always first
  gemini: "candidates.0.content.parts.0.text",
};

function extractText(style: AiBodyStyle, data: any, responsePath?: string): string {
  if (responsePath) {
    const value = readPath(data, responsePath);
    if (typeof value === "string") return value;
    throw new Error(`No text found at response path "${responsePath}".`);
  }

  if (style === "anthropic") {
    // `content` interleaves thinking and text blocks; index 0 is not reliably
    // the answer, so find the text block rather than assuming a position.
    const block = Array.isArray(data?.content)
      ? data.content.find((b: any) => b?.type === "text")
      : null;
    if (typeof block?.text === "string") return block.text;
    throw new Error("The AI returned no text content.");
  }

  const value = readPath(data, DEFAULT_RESPONSE_PATHS[style]);
  if (typeof value === "string") return value;
  throw new Error("The AI returned no text content.");
}

export interface AiClient {
  providerKey: string;
  providerLabel: string;
  model: string;
  /** Sends one prompt and returns the raw text of the reply. */
  complete(system: string, user: string, signal?: AbortSignal): Promise<string>;
  /** Same, but with the fence/trailing-comma repair applied. */
  completeJson(system: string, user: string, signal?: AbortSignal): Promise<string>;
}

export function createAiClient(opts: {
  providerKey: string;
  providerLabel: string;
  bodyStyle: AiBodyStyle;
  url: string;
  apiKey: string;
  authHeader: string;
  authPrefix: string;
  extraHeaders?: Record<string, string>;
  model: string;
  maxTokens: number;
  responsePath?: string;
}): AiClient {
  const url = opts.url.replace("{model}", encodeURIComponent(opts.model)).replace("{key}", encodeURIComponent(opts.apiKey));

  const headers: Record<string, string> = { ...opts.extraHeaders };
  if (opts.authHeader) headers[opts.authHeader] = `${opts.authPrefix}${opts.apiKey}`;

  const cfg: AiRequestConfig = {
    url,
    headers,
    model: opts.model,
    maxTokens: opts.maxTokens,
    responsePath: opts.responsePath,
  };

  async function complete(system: string, user: string, signal?: AbortSignal): Promise<string> {
    const data = await aiFetch(url, { headers, body: buildBody(opts.bodyStyle, cfg, system, user) }, signal);
    return extractText(opts.bodyStyle, data, opts.responsePath);
  }

  return {
    providerKey: opts.providerKey,
    providerLabel: opts.providerLabel,
    model: opts.model,
    complete,
    completeJson: async (system, user, signal) => cleanJsonResponse(await complete(system, user, signal)),
  };
}
